package com.fantasytenis.shared.domain.scoring.entity

import com.fantasytenis.shared.domain.lineup.entity.Category
import kotlinx.serialization.Serializable

/**
 * Category score result from scoring calculation
 */
@Serializable
data class CategoryScoreResult(
    val category: String,
    val playerId: String,
    val faceValue: Double,
    val quickFinishBonus: Double,
    val totalScore: Double
) {
    val categoryEnum: Category?
        get() = Category.fromString(category)
}

/**
 * Lineup score result with breakdown
 */
@Serializable
data class LineupScoreResult(
    val lineupId: String,
    val serve: CategoryScoreResult,
    val forehand: CategoryScoreResult,
    val backhand: CategoryScoreResult,
    val volleys: CategoryScoreResult,
    val fighting: CategoryScoreResult,
    val totalScore: Double,
    val totalFaceValue: Double,
    val totalQuickFinishBonus: Double
) {
    /**
     * Get score for a specific category
     */
    fun getScoreForCategory(category: Category): CategoryScoreResult {
        return when (category) {
            Category.SERVE -> serve
            Category.FOREHAND -> forehand
            Category.BACKHAND -> backhand
            Category.VOLLEYS -> volleys
            Category.FIGHTING -> fighting
        }
    }
}

/**
 * Scoring rules configuration
 */
@Serializable
data class ScoringRules(
    // Serve category
    val acePoints: Int = 3,
    val doubleFaultPoints: Int = -1,
    // Forehand category
    val forehandWinnerPoints: Int = 2,
    val forehandErrorPoints: Int = -1,
    // Backhand category
    val backhandWinnerPoints: Int = 2,
    val backhandErrorPoints: Int = -1,
    // Volleys category
    val volleyWinnerPoints: Int = 3,
    // Fighting category
    val breakPointConvertedPoints: Int = 4,
    val breakPointSavedPoints: Int = 2,
    // Quick finish bonus: alpha × max(0, maxSets - setsPlayed)
    val quickFinishAlpha: Int = 10
) {
    companion object {
        /**
         * Default scoring rules
         */
        val DEFAULT = ScoringRules()
    }
}

/**
 * Match statistics for scoring
 */
@Serializable
data class MatchStatisticsEntity(
    val id: String,
    val matchId: String,
    val playerId: String,
    // Service stats
    val aces: Int = 0,
    val doubleFaults: Int = 0,
    val firstServePct: Double? = null,
    val servePoints: Int = 0,
    val servePointsWon: Int = 0,
    // Winners/Errors
    val forehandWinners: Int = 0,
    val forehandErrors: Int = 0,
    val backhandWinners: Int = 0,
    val backhandErrors: Int = 0,
    val volleyWinners: Int = 0,
    val totalWinners: Int = 0,
    val totalUnforcedErrors: Int = 0,
    // Break points
    val breakPointsConverted: Int = 0,
    val breakPointsSaved: Int = 0,
    val breakPointsFaced: Int = 0,
    // Match info
    val setsPlayed: Int = 0,
    val maxSets: Int = 3, // Match format (3 for best-of-3, 5 for best-of-5)
    val totalPointsPlayed: Int = 0,
    val isWinner: Boolean = false
) {
    /**
     * Calculate serve category score
     */
    fun calculateServeScore(rules: ScoringRules = ScoringRules.DEFAULT): Double {
        return (rules.acePoints * aces + rules.doubleFaultPoints * doubleFaults).toDouble()
    }

    /**
     * Calculate forehand category score
     */
    fun calculateForehandScore(rules: ScoringRules = ScoringRules.DEFAULT): Double {
        return (rules.forehandWinnerPoints * forehandWinners +
                rules.forehandErrorPoints * forehandErrors).toDouble()
    }

    /**
     * Calculate backhand category score
     */
    fun calculateBackhandScore(rules: ScoringRules = ScoringRules.DEFAULT): Double {
        return (rules.backhandWinnerPoints * backhandWinners +
                rules.backhandErrorPoints * backhandErrors).toDouble()
    }

    /**
     * Calculate volleys category score
     */
    fun calculateVolleysScore(rules: ScoringRules = ScoringRules.DEFAULT): Double {
        return (rules.volleyWinnerPoints * volleyWinners).toDouble()
    }

    /**
     * Calculate fighting category score
     */
    fun calculateFightingScore(rules: ScoringRules = ScoringRules.DEFAULT): Double {
        return (rules.breakPointConvertedPoints * breakPointsConverted +
                rules.breakPointSavedPoints * breakPointsSaved).toDouble()
    }

    /**
     * Calculate quick finish bonus based on sets played vs match format
     *
     * Formula: alpha × max(0, maxSets - setsPlayed)
     *
     * Best-of-3: 2 sets => +10, 3 sets => +0
     * Best-of-5: 3 sets => +20, 4 sets => +10, 5 sets => +0
     */
    fun calculateQuickFinishBonus(rules: ScoringRules = ScoringRules.DEFAULT): Double {
        if (setsPlayed <= 0) return 0.0
        if (maxSets != 3 && maxSets != 5) return 0.0
        return (rules.quickFinishAlpha * maxOf(0, maxSets - setsPlayed)).toDouble()
    }

    /**
     * Calculate score for a specific category
     */
    fun calculateCategoryScore(
        category: Category,
        rules: ScoringRules = ScoringRules.DEFAULT
    ): Double {
        val faceValue = when (category) {
            Category.SERVE -> calculateServeScore(rules)
            Category.FOREHAND -> calculateForehandScore(rules)
            Category.BACKHAND -> calculateBackhandScore(rules)
            Category.VOLLEYS -> calculateVolleysScore(rules)
            Category.FIGHTING -> calculateFightingScore(rules)
        }
        return faceValue + calculateQuickFinishBonus(rules)
    }
}
